import React from 'react';
import { Link } from 'react-router';
import capitalize from 'lodash/capitalize';
import ProjectLink from 'common/containers/project_link';
import { ELITE_TIPPED_SPOTS_TEXT } from 'project/constants';

export const getSlides = ({ project, entryName, creativeName }) => [{
  title: "Your project is live",
  description: "What happens next?",
  show: true
}, {
  title: (
    project.is_one_to_one? `See ${entryName}s.` : `${capitalize(creativeName)}s will submit ${entryName}s.`
  ),
  description: <div>
    { project.is_one_to_one &&
    <p>
      Your chosen { creativeName } needs a little time to come up with creative ideas. Soon,
      they'll start submitting custom { entryName }s for your review! We'll email you when a
      new { entryName } is submitted.
    </p> }

    { project.elite_only &&
    <p>
      You're working with 5 of our best designers. They need a little time to come up with
      creative ideas. Soon, they'll start submitting custom designs for your review! We'll
      email you when a new design is submitted.
    </p> }

    { project.entry_type === "name" && !project.is_one_to_one &&
    <p>
      Creatives need a little time to come up with great { entryName }s. Soon, they'll
      start submitting custom { entryName }s for your review! We'll email you when a
      new { entryName } is submitted.
    </p> }

    { project.entry_type === "image" && !project.elite_only && !project.is_one_to_one &&
    <p>
      Designers need a little time to come up with creative ideas. Soon, they'll start submitting
      custom designs for your review! We'll email you when a new design is submitted.
    </p> }

    <p>
      You can change what notifications you receive, and how often,
      in <Link target="_blank" to="/account/notifications/">your account</Link>.
    </p>
  </div>,
  image: (
    project.entry_type === "image"?
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step1.jpg" :
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/naming-tour-step1.jpg"
  ),
  show: true
}, {
  title: "Score & give feedback.",
  description: <div>
    <p>
      Scoring and giving feedback on { entryName }s is easy, fun, and the best way to
      improve { entryName }s { project.is_one_to_one? "" : " and increase participation" }.
    </p>
    <p>
      Comment and score { entryName }s from 1 to 5 starts (5 is best). Comments and scores
      are private. Only you and the entry's creator can see them.
    </p>
  </div>,
  image: (
    project.entry_type === "image"?
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step2.jpg" :
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/naming-tour-step2.png"
  ),
  show: true
}, {
  title: "Flexible project views.",
  description: <div>
    { project.entry_type === "image"?
    <p>
      View designs as tiles (default view) or in a "list" format (more details). Click
      a design to see all comments and details.
    </p> :
    <p>
      View { entryName }s in a column view (default view) or in a "list" format (more details).
      Click a { entryName } to see all comments and details.
    </p> }

    { project.entry_type === "image"?
    <p>
      Hover over any design and a three dot menu on the top left of the tile will show you more
      tools.
    </p> :
    <p>
      A three dot menu on the right (column view) or the top left of a { entryName } (list view)
      will show you more tools.
    </p> }
  </div>,
  image: (
    project.entry_type === "image"?
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step3.png" :
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/naming-tour-step3.png"
  ),
  show: true
}, {
  title: "Useful project tools.",
  description: <div>
    { project.entry_type === "image"?
    <p>
      View individual designs or group revisions (designs with minor tweaks), sort and
      filter designs based on score, and see new designs using the tools on the right in your
      Entries gallery.
    </p> :
    <p>
      Sort and filter { entryName }s based on score, and see new { entryName }s using the tools
      on the right of your Entries gallery.
    </p> }
  </div>,
  image: (
    project.entry_type === "image"?
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step4.jpg" :
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/naming-tour-step4.png"
  ),
  show: true
}, {
  title: "See a smile or frown?",
  description: <div>
    <p>
      A smiley face icon in your project means you're doing a great job scoring and giving
      feedback. Click that icon to compare your project to similar projects.
    </p>
    <p>
      Remember, you'll get better { entryName }s (and more of them) if your project shows
      a smiley face!
    </p>
  </div>,
  image: "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step5.jpg",
  show: true
}, {
  title: `See revised ${entryName}s.`,
  description: <div>
    { project.is_one_to_one?
    <p>
      If you score and give feedback promptly, your { creativeName } will have time to submit
      revised { entryName }s for your review. You can work with your { creativeName } to get
      the { entryName } you love!
    </p> :
    <p>
      If you score and give feedback promptly, { creativeName }s will have time to submit
      revised { entryName }s for your review. You can work with each { creativeName } to get
      the { entryName } you love!
    </p> }

    { !project.is_one_to_one &&
    <p>
      Send all participants a public message by leaving an "update" in the Brief section of
      your project.
    </p> }
  </div>,
  image: (
    project.entry_type === "image"?
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step6.jpg" :
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/naming-tour-step6.png"
  ),
  show: true
}, {
  title: "Launch focus groups.",
  description: <div>
    <p>
      Pick up to 12 designs and invite colleagues, customers, and others to comment
      and vote on their favorites.
    </p>
    <p>
      <Link target="_blank" to="/help/managing-project/what-is-a-focus-group/">Focus
      groups</Link> work best when your project closes and all designs have been
      submitted, but you can launch one anytime.
    </p>
  </div>,
  image: "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step7.jpg",
  show: project.entry_type === "image"
}, {
  title: "Assure your awards.",
  description: <div>
    <p>
      Assuring your project is your promise that someone will win the award. This shows
      that you're a commited client and will increase the quality of { entryName }s and participation
      in your project.
    </p>
    <p>
      It's best to assure awards when the project starts, but you can do it any time your
      project is open.
    </p>
  </div>,
  image: "https://s3.amazonaws.com/crowdspring3-assets/marketing/naming-tour-step7.jpg",
  show: project.entry_type === "name"
}, {
  title: "Adjust awards and tips.",
  description: <div>
    <p>
      Increase or add more awards while your project is open. Make offers
      on { entryName }s you like after you pick the winner.
      { project.entry_type === "name" || project.is_one_to_one? "" :
      " Tip any non-winning designer to thank them for their above and beyond work." }
    </p>

    { project.elite_only &&
    <p>
      Remember, the winner will receive the award. The other {ELITE_TIPPED_SPOTS_TEXT} designers
      will get a guaranteed tip (already included in the cost of your project).
    </p> }

    { project.entry_type === "image" && !project.is_one_to_one && !project.elite_only &&
    <p>
      Consider <Link target="_blank"
      to="/help/creating-project/what-does-it-mean-when-a-project-is-client-assured/">assuring
      </Link> your award. That means you promise that someone will win. Assured projects get higher
      quality designs and participants.
    </p> }

    { project.entry_type === "name" && !project.is_one_to_one &&
    <p>
      You can tip any non-winning creative to thank them for their above and beyond work
      in your project.
    </p> }
  </div>,
  image: (
    project.entry_type === "image"?
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step8.png" :
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/naming-tour-step8.jpg"
  ),
  show: true
}, {
  title: "Easy project extensions.",
  description: <div>
    <p>
      Need more time to see new { entryName }s and revisions? Extend your project by up
      to 7 days. Need more than 7 days? Please <Link target="_blank" to="/help/contact/">
      contact customer support</Link>.
    </p>
    <p>
      Once your project closes, you'll have up to 7 days to pick the winner. Need more
      time to pick the winner? Extend by up to 7 more days.
    </p>
  </div>,
  image: "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step9.jpg",
  show: true
}, {
  title: `Pick the winning ${entryName}.`,
  description: <div>
    <p>
      When you're ready to wrap-up, award your favorite { entryName } and sign the contract.
      The contract transfers full intellectual property rights to you.
    </p>
    { ['open', 'closed'].includes(project.status) &&
    <p>
      To learn about wrap-up in your project, take the tour in
      the <ProjectLink target="_blank" project={project} append="wrapup/intro/">
      wrap-up tab of your project</ProjectLink>.
    </p> }
  </div>,
  image: (
    project.entry_type === "image"?
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/design-tour-step10.jpg" :
    "https://s3.amazonaws.com/crowdspring3-assets/marketing/naming-tour-step10.png"
  ),
  show: true
}, {
  title: "That's it!",
  description: <div>
    <p>
      Have fun and remember that by scoring and giving feedback to
      all { entryName }s, { project.elite_only || project.is_one_to_one ? <span> you will get
      better { entryName }s.</span> : <span> you will get twice as many entries - and better
      ones!</span> }
    </p>
    <p>
      If your have any questions, please check out our FAQs on <Link target="_blank"
      to="/help/guides/5-tips-crowdspring-project/">managing your project</Link>,
      or <Link target="_blank" to="/help/contact/">contact our customer support</Link> team.
    </p>
  </div>,
  image: "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_support-2x.png",
  show: true
}]



// WEBPACK FOOTER //
// ./project/components/tour_slides.jsx